import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

import type { StatusTone } from '../../utils/run-status';

import { TONE_ICON, TONE_SURFACE, TONE_TEXT } from './tones';

interface HelpCalloutProps {
  /** One plain sentence — the thing the reader must know. */
  children: ReactNode;
  /** Optional bold lead-in before the sentence. */
  title?: string;
  /** Longer explanation, hidden behind "Learn more". */
  details?: ReactNode;
  /** `info` explains, `warning`/`error` alert, `success` confirms, `muted` is a quiet note. */
  tone?: StatusTone;
  /** Right-aligned action, e.g. "Re-sync". */
  action?: ReactNode;
  className?: string;
}

/**
 * The ONE way the Content section explains or alerts: icon + a sentence, with
 * optional "Learn more". Replaces the mix of info boxes, intro cards and
 * collapsibles, so explanations look and behave the same everywhere.
 */
export function HelpCallout({
  children,
  title,
  details,
  tone = 'info',
  action,
  className,
}: HelpCalloutProps) {
  const [open, setOpen] = useState(false);
  const Icon = TONE_ICON[tone];
  return (
    <div
      role={tone === 'error' || tone === 'warning' ? 'alert' : 'note'}
      className={cn(
        'flex items-start gap-3 rounded-2xl border px-4 py-3',
        TONE_SURFACE[tone],
        className,
      )}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', TONE_TEXT[tone])} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-foreground text-sm leading-relaxed">
          {title && <span className="font-semibold">{title} </span>}
          {children}
          {details && (
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setOpen((v) => !v);
              }}
              aria-expanded={open}
              className="ml-1 h-auto cursor-pointer p-0 align-baseline text-sm"
            >
              {open ? 'Show less' : 'Learn more'}
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  'ml-0.5 size-3.5 transition-transform motion-reduce:transition-none',
                  open && 'rotate-180',
                )}
              />
            </Button>
          )}
        </p>
        {details && open && (
          <div className="text-muted-foreground text-sm leading-relaxed">{details}</div>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
